import { flexRender, type Header, type RowData as TanStackRowData } from "@tanstack/react-table"
import clsx from "clsx"
import * as React from "react"
import { TableHead, TableHeader, TableRow } from "../ui/table"
import { useDataTableCtx } from "./DataTable"
import { DataTablePinnedShadow } from "./DataTablePinnedShadow"
import type { DataTableScrollState } from "./types"

export function DataTableHeader<TRowData extends TanStackRowData>({
  scrollState,
}: {
  scrollState: DataTableScrollState
}) {
  const { appliedPlugins, table } = useDataTableCtx<TRowData>()
  function getMergedHeaderProps(header: Header<TRowData, unknown>) {
    return appliedPlugins
      .map((plugin) => plugin.getHeaderProps?.(header, scrollState) || {})
      .reduce((acc, props) => {
        // Merge className and style, shallow merge for others
        if (props.className) {
          acc.className = acc.className ? `${acc.className} ${props.className}` : props.className
        }
        if (props.style) {
          acc.style = { ...acc.style, ...props.style }
        }
        // biome-ignore lint/performance/noAccumulatingSpread: later
        return { ...acc, ...props }
      }, {})
  }

  return (
    <TableHeader>
      <TableRow>
        {table.getLeftFlatHeaders().map((header) => {
          const isSelectColumn = header.column.id === "__select__"
          const headerProps = getMergedHeaderProps(header)
          return (
            <TableHead
              key={header.id}
              className={clsx("sticky left-0 z-10", headerProps.className, {
                "!bg-neutral-0": isSelectColumn,
              })}
              style={{
                width: header.column.getSize(),
                left: header.column.getStart(),
                ...(headerProps.style || {}),
              }}
            >
              {header.isPlaceholder ? null : (
                <DataTablePinnedShadow isHorizontalScroll={scrollState.isHorizontalScroll}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </DataTablePinnedShadow>
              )}
            </TableHead>
          )
        })}

        {table.getCenterFlatHeaders().map((header) => {
          const headerProps = getMergedHeaderProps(header)
          return (
            <TableHead className={headerProps.className} key={header.id} style={headerProps.style}>
              {header.isPlaceholder
                ? null
                : flexRender(header.column.columnDef.header, header.getContext())}
            </TableHead>
          )
        })}

        {table.getRightFlatHeaders().map((header) => (
          <RightTableHead
            key={header.id}
            header={header}
            scrollState={scrollState}
            getMergedHeaderProps={getMergedHeaderProps}
          />
        ))}
      </TableRow>
    </TableHeader>
  )
}

function RightTableHead<TRowData extends TanStackRowData>({
  header,
  scrollState,
  getMergedHeaderProps,
}: {
  header: Header<TRowData, unknown>
  scrollState: DataTableScrollState
  getMergedHeaderProps: (header: Header<TRowData, unknown>) => {
    className?: string
    style?: React.CSSProperties
  }
}) {
  const { scrollLeft, scrollWidth } = scrollState
  const headerProps = getMergedHeaderProps(header)
  const headerRef = React.useRef<HTMLTableCellElement>(null)
  const headerWidth = headerRef.current?.clientWidth ?? 0

  return (
    <TableHead
      ref={headerRef}
      className={clsx("sticky right-0 z-10 bg-[transparent]", headerProps.className, {
        "!bg-[--wb-table-header-background-default]": scrollLeft >= scrollWidth - headerWidth,
      })}
      style={headerProps.style}
    >
      {header.isPlaceholder
        ? null
        : flexRender(header.column.columnDef.header, header.getContext())}
    </TableHead>
  )
}
